import React, { FormEvent, useEffect, useState } from 'react';
import { Button } from '@mui/material';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import Appbar from 'components/appbar/Appbar';
import InsideSaving from 'components/loading/InsideSaving';
import {
  ContentPad,
  PageShell,
  RecordFooter,
  RecordHeader,
  StyledForm,
  WorkArea,
} from 'components/record-shell/RecordShell';
import { api } from 'services/api';
import history from 'services/history';
import { Package, PackageProduct } from 'types/package';
import PackageProducts from '../PackageProducts';
import PackageForm from '../form/PackageForm';
import PackageTab from '../tab/PackageTab';
import { usePackageValidation } from '../validation/usePackageValidation';

const NewPackage: React.FC = () => {
  const [validation, setValidation, validate] = usePackageValidation();
  const [tab, setTab] = useState('main');
  const [saving, setSaving] = useState(false);
  const [pkg, setPkg] = useState<Package>({
    id: 0,
    name: '',
    cost_price: 0,
    sale_price_max: 0,
    sale_price_min: 0,
    products: [],
    supplier: null,
    supplier_id: 0,
    active: true,
  });

  // a validação guarda o erro do campo; abre a aba onde ele está
  useEffect(() => {
    if (Object.keys(validation).length > 0) setTab('main');
  }, [validation]);

  function goBack() {
    history.push('/packages');
  }

  function handleChange<K extends keyof Package>(index: K, value: Package[K]) {
    setPkg(prev => ({ ...prev, [index]: value }));
  }

  function handleSubmit() {
    setSaving(true);

    api
      .post('/packages', pkg)
      .then(response => {
        toast.success('Pacote cadastrado');
        // abre o pacote recém-cadastrado
        history.push(response.data?.id ? `/packages/${response.data.id}` : '/packages');
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível cadastrar o pacote');
      })
      .finally(() => setSaving(false));
  }

  function handleValidation(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();

    validate(pkg)
      .then(() => {
        handleSubmit();
        setValidation({});
      })
      .catch((err: yup.ValidationError) => {
        if (err instanceof yup.ValidationError) {
          setValidation({
            [err.path as string]: err.message,
          });
        }
      });
  }

  // produtos ficam na memória até o pacote ser salvo
  function handleChangeAddProduct(product: PackageProduct) {
    setPkg(prev => {
      const existingProduct = prev.products.find(b => b.id === product.id);
      if (existingProduct) {
        return {
          ...prev,
          products: prev.products.map(b => (b.id === product.id ? { ...b, ...product } : b)),
        };
      }
      return {
        ...prev,
        products: [
          ...prev.products,
          { ...product, id: prev.products.length ? Math.max(...prev.products.map(b => b.id)) + 1 : 1 },
        ],
      };
    });
  }

  function handleDeleteProduct(product: PackageProduct) {
    setPkg(prev => ({
      ...prev,
      products: prev.products.filter(b => b.id !== product.id),
    }));
  }

  return (
    <>
      <Appbar title="Pacotes" backAction={goBack} />

      {saving && <InsideSaving />}

      <PageShell>
        <RecordHeader title="Novo pacote" backAction={goBack} />

        <WorkArea>
          <PackageTab onChange={newTab => setTab(newTab)} tab={tab} productsCount={pkg.products.length} />
          <ContentPad>
            {tab === 'products' ? (
              <PackageProducts
                pkg={pkg}
                handleDeleteProduct={handleDeleteProduct}
                handleChangeAddProduct={handleChangeAddProduct}
              />
            ) : (
              <StyledForm onSubmit={handleValidation}>
                <PackageForm handleChange={handleChange} pkg={pkg} validation={validation} />
                <button type="submit" style={{ display: 'none' }}></button>
              </StyledForm>
            )}
          </ContentPad>
        </WorkArea>

        <RecordFooter>
          <Button variant="contained" size="small" disabled={saving} onClick={() => handleValidation()}>
            Salvar pacote
          </Button>
        </RecordFooter>
      </PageShell>
    </>
  );
};

export default NewPackage;
